import React from 'react'
import { Box, Button, IconButton, MenuItem, Select } from '@mui/material'

import { colors } from '~tokens/internal'

import Icon from './Icon'
import Typography from './Typography'

interface TablePaginationProps {
  totalCount: number
  currentPage: number
  pageSize: number
  /**
   * When true, the pagination footer is always rendered (page-size selector +
   * item count) even when the total fits in a single page. Page navigation
   * buttons are still hidden when there is only one page.
   */
  alwaysShow?: boolean
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  pageSizeOptions?: number[]
  itemLabel?: string
  disableSkipToEnd?: boolean
}

const TablePagination: React.FC<TablePaginationProps> = ({
  totalCount,
  currentPage,
  pageSize,
  alwaysShow = false,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50],
  itemLabel = 'items',
  disableSkipToEnd = false,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const hasMultiplePages = totalPages > 1

  if (!alwaysShow && totalCount <= Math.min(...pageSizeOptions)) {
    return null
  }

  // Calculate which page numbers to show (max 5, centered around current page)
  const getPageNumbers = (): number[] => {
    const pages: number[] = []
    let start = Math.max(1, currentPage - 2)
    let end = Math.min(totalPages, start + 4)

    if (end - start < 4) {
      start = Math.max(1, end - 4)
    }

    for (let i = start; i <= end; i++) {
      pages.push(i)
    }
    return pages
  }

  const pageNumbers = getPageNumbers()

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 2,
        px: 1,
      }}
    >
      {/* Page Size Selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant='body2' sx={{ color: colors.neutral[600] }}>
          Showing
        </Typography>
        <Select
          value={pageSize}
          onChange={(e) => {
            onPageSizeChange(Number(e.target.value))
            onPageChange(1) // Reset to first page when changing page size
          }}
          size='small'
          sx={{
            minWidth: 70,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.neutral[300] },
          }}
        >
          {pageSizeOptions.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
        <Typography variant='body2' sx={{ color: colors.neutral[600] }}>
          of {totalCount} {itemLabel}
        </Typography>
      </Box>

      {hasMultiplePages && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {!disableSkipToEnd && (
            <IconButton
              size='small'
              disabled={currentPage === 1}
              onClick={() => onPageChange(1)}
              sx={{ color: colors.neutral[600] }}
            >
              <Icon name='doubleArrowLeft' />
            </IconButton>
          )}
          <IconButton
            size='small'
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
            sx={{ color: colors.neutral[600] }}
          >
            <Icon name='chevronLeft' />
          </IconButton>
          {pageNumbers.map((page) => (
            <Button
              key={page}
              size='small'
              onClick={() => onPageChange(page)}
              sx={{
                minWidth: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: currentPage === page ? colors.primary[500] : 'transparent',
                color: currentPage === page ? 'white' : colors.neutral[700],
                '&:hover': {
                  bgcolor: currentPage === page ? colors.primary[600] : colors.neutral[100],
                },
              }}
            >
              {page}
            </Button>
          ))}
          <IconButton
            size='small'
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            sx={{ color: colors.neutral[600] }}
          >
            <Icon name='chevronRight' />
          </IconButton>
          {!disableSkipToEnd && (
            <IconButton
              size='small'
              disabled={currentPage === totalPages}
              onClick={() => onPageChange(totalPages)}
              sx={{ color: colors.neutral[600] }}
            >
              <Icon name='doubleArrowRight' />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  )
}

export default TablePagination
